import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleDashed, Flag, Undo2 } from 'lucide-react';
import { api, parseApiError, type MatchSignoffState } from '../lib/api';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// SignoffPanel — team-scoped result sign-off (stage 2, Lisa 2026-07-22).
//
// Each side signs off its OWN rows. The operator's verify then becomes a
// confirmation of two sign-offs rather than a lone judgement call — which is
// the only version of this that scales past one person per season.
//
// WHO can press what is enforced SERVER-side (shared/matchSignoff.ts:
// that side's clan leader, the captain of the team that side fielded, or an
// operator on an unresponsive side's behalf). The `canSign1`/`canSign2` props
// here are UX gating only — the same posture every other control on the match
// card takes.
//
// "Something's wrong" is deliberately NOT a new dispute path: it points at the
// existing score-flag mechanism, which already routes to the admin inbox and
// already carries the per-cell detail an operator needs. One dispute route, not
// two.
// ─────────────────────────────────────────────────────────────────────────────

export function SignoffPanel({
  matchId, compId, signoff, clan1Label, clan2Label, canSign1, canSign2, myDiscordId, onFlagRequested,
}: {
  matchId: string;
  compId: string;
  signoff?: MatchSignoffState;
  clan1Label: string;
  clan2Label: string;
  canSign1: boolean;
  canSign2: boolean;
  myDiscordId?: string;
  /** Scrolls/pulses the scoreboard so the flag buttons are found. */
  onFlagRequested?: () => void;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: ({ side, action }: { side: 1 | 2; action: 'SIGN' | 'WITHDRAW' }) =>
      api.matchSignoff(matchId, compId, side, action),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
  });

  // Nothing to offer and nothing to report — don't take up space on a card
  // whose viewer has no part in this.
  if (!canSign1 && !canSign2 && !signoff?.side1 && !signoff?.side2) return null;

  const rows: Array<{ side: 1 | 2; label: string; canSign: boolean }> = [
    { side: 1, label: clan1Label, canSign: canSign1 },
    { side: 2, label: clan2Label, canSign: canSign2 },
  ];

  return (
    <div className="rounded-xl bg-paper-panel px-4 py-3.5">
      <div className="mb-3 flex items-baseline gap-2">
        <h3 className="font-display text-xs font-black uppercase tracking-[0.24em] text-ink-secondary">
          Result sign-off
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-meta">
          {signoff?.state === 'READY' ? 'both sides in'
            : signoff?.state === 'AWAITING_ONE' ? `waiting on ${signoff.awaitingSide === 1 ? clan1Label : clan2Label}`
            : 'awaiting both sides'}
        </span>
      </div>

      <div className="space-y-2">
        {rows.map(({ side, label, canSign }) => {
          const state = side === 1 ? signoff?.side1 : signoff?.side2;
          const signed = state?.status === 'SIGNED';
          const mine = signed && state?.signedBy === myDiscordId;
          const busy = mut.isPending && mut.variables?.side === side;

          return (
            <div key={side} className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-white/[0.03] px-3 py-2.5">
              {signed
                ? <CheckCircle2 className="h-4 w-4 shrink-0 text-accent-500" strokeWidth={2.25} />
                : <CircleDashed className="h-4 w-4 shrink-0 text-ink-meta" strokeWidth={2} />}
              <span className="font-display text-[12px] font-bold uppercase tracking-wider text-ink-primary">{label}</span>
              <span className="font-mono text-[10px] text-ink-meta">
                {signed
                  ? (state?.onBehalf ? 'signed on their behalf' : 'signed')
                  : 'not signed yet'}
              </span>

              <div className="ml-auto flex items-center gap-2">
                {canSign && !signed && (
                  <>
                    <button
                      onClick={() => mut.mutate({ side, action: 'SIGN' })}
                      disabled={busy}
                      className={cn(
                        'rounded-lg px-3 py-1.5 font-display text-[11px] font-black uppercase tracking-wider transition-colors disabled:opacity-50',
                        'bg-accent-500 text-[#1a1206] hover:brightness-110',
                      )}
                    >
                      {busy ? 'Saving…' : 'Looks right'}
                    </button>
                    {/* Points at the EXISTING flag mechanism rather than opening
                        a second dispute path — see this file's header. */}
                    <button
                      onClick={onFlagRequested}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] px-3 py-1.5 font-display text-[11px] font-black uppercase tracking-wider text-ink-secondary transition-colors hover:bg-white/[0.1] hover:text-ink-primary"
                    >
                      <Flag className="h-3 w-3" strokeWidth={2.5} />
                      Something's wrong
                    </button>
                  </>
                )}
                {/* Revocable until an operator verifies — a hasty tap must not
                    become a dispute that needs an admin. Only the person who
                    signed is offered the undo. */}
                {canSign && signed && mine && (
                  <button
                    onClick={() => mut.mutate({ side, action: 'WITHDRAW' })}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] px-3 py-1.5 font-display text-[11px] font-black uppercase tracking-wider text-ink-meta transition-colors hover:bg-white/[0.1] hover:text-ink-primary disabled:opacity-50"
                  >
                    <Undo2 className="h-3 w-3" strokeWidth={2.5} />
                    {busy ? 'Saving…' : 'Undo'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {mut.isError && (
        <p className="mt-2 font-mono text-[10px] text-red-300">
          {parseApiError((mut.error as Error).message).message}
        </p>
      )}
    </div>
  );
}
